const delay = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds))

const SELECTORS = {
    condicaoNova: '[data-tour="automacao-condicao-nova"]',
    condicaoItem: '[data-tour="automacao-condicao-item"]',
    condicaoOu: '[data-tour="automacao-condicao-ou"]',
    condicaoAutomacaoNova: '[data-tour="automacao-condicao-automacao-nova"]',
    condicaoAutomacaoItem: '[data-tour="automacao-condicao-automacao-item"]',
    acaoPositivaNova: '[data-tour="automacao-acao-positiva-nova"]',
    acaoPositivaItem: '[data-tour="automacao-acoes-positivas"] .automacao-alerta-condicao',
    intervaloNovo: '[data-tour="automacao-intervalo-novo"]',
    intervaloItem: '[data-tour="automacao-intervalos"] .intervalo-item',
    intervaloVolume: '[data-tour="automacao-intervalo-volume"]',
}

const activatePanel = async (panelName) => {
    const stepElement = document.querySelector(`[data-tour="automacao-step-${panelName}"]`)
    stepElement?.click()
    await delay(180)
}

const moveToPanelAndNext = (tourManager, panelName) => {
    activatePanel(panelName).then(() => tourManager.moveNext())
}

const moveToPanelAndPrevious = (tourManager, panelName) => {
    activatePanel(panelName).then(() => tourManager.movePrevious())
}

const ensureExists = async (tourManager, buttonSelector, itemSelector) => {
    if (document.querySelector(itemSelector)) return

    document.querySelector(buttonSelector)?.click()

    try {
        await tourManager.waitForElement(itemSelector, 2500)
    } catch (error) {}
}

const bindClickToAdvance = (element, tourManager, itemSelector) => {
    if (!element || element._hdvTourAdvance) return

    const onClick = () => {
        const advance = async () => {
            if (itemSelector) {
                try {
                    await tourManager.waitForElement(itemSelector, 2500)
                } catch (error) {}
            } else {
                await delay(220)
            }

            if (tourManager.isActive()) tourManager.moveNext()
        }

        advance()
    }

    element.addEventListener('click', onClick)
    element._hdvTourAdvance = onClick
}

const unbindClickToAdvance = (element) => {
    if (!element?._hdvTourAdvance) return

    element.removeEventListener('click', element._hdvTourAdvance)
    delete element._hdvTourAdvance
}

const goNextCreating = (tourManager, buttonSelector, itemSelector) => {
    unbindClickToAdvance(document.querySelector(buttonSelector))
    ensureExists(tourManager, buttonSelector, itemSelector)
        .then(() => tourManager.moveNext())
}

const novaButtonStep = (tourManager, { element, itemSelector, title, description, panel, onPrevClick, onNextClick }) => ({
    element,
    panel,
    popover: {
        title,
        description,
        side: 'bottom',
        align: 'start',
        onNextClick: onNextClick || (() => goNextCreating(tourManager, element, itemSelector)),
        onPrevClick,
    },
    onHighlighted: (highlightedElement) => bindClickToAdvance(highlightedElement, tourManager, itemSelector),
    onDeselected: (highlightedElement) => unbindClickToAdvance(highlightedElement),
})

const buildFormSteps = (tourManager, isEditing, { indiceNovoStep } = {}) => {
    const formSteps = [
        {
            element: '[data-tour="automacao-stepper"]',
            panel: 'condicoes',
            popover: {
                title: 'Etapas do cadastro',
                description: 'O cadastro é dividido em quatro etapas: Condições, Ações, Extras e Simulação. Use este indicador para acompanhar seu progresso ou acessar outra etapa do cadastro.',
                side: 'bottom',
                align: 'center',
                ...(Number.isInteger(indiceNovoStep)
                    ? {
                        onPrevClick: () => {
                            tourManager.navigateAndWait('/automacao', '[data-tour="automacao-novo"]', indiceNovoStep, 'novo-automacao')
                                .then(() => tourManager.moveTo(indiceNovoStep))
                                .catch(() => {})
                        },
                    }
                    : {}),
            },
        },
        {
            element: '[data-tour="automacao-descricao"]',
            panel: 'condicoes',
            popover: {
                title: 'Descrição',
                description: isEditing
                    ? 'A descrição explica a finalidade desta automação e ajuda a identificá-la na lista.'
                    : 'Informe um nome que deixe clara a finalidade da automação. Ele será usado para identificá-la na lista.',
                side: 'bottom',
                align: 'start',
            },
        },
        {
            element: '[data-tour="automacao-ativo"]',
            panel: 'condicoes',
            popover: {
                title: 'Ativar automação',
                description: isEditing
                    ? 'Indica se a automação está ativa. Quando desativada, ela não será executada, mesmo que as condições sejam atendidas. É necessário ativá-la novamente para que volte a funcionar.'
                    : 'Quando ativada, a automação pode ser executada normalmente. Quando desativada, ela não será executada, mesmo que as condições sejam atendidas. É necessário ativá-la novamente para que volte a funcionar.',
                side: 'bottom',
                align: 'start',
            },
        },
        {
            element: '[data-tour="automacao-ordem"]',
            panel: 'condicoes',
            popover: {
                title: 'Ordem de execução',
                description: 'Define a sequência em que as automações serão avaliadas. Quanto menor o número, maior a prioridade. Por padrão, o valor é 0.',
                side: 'bottom',
                align: 'start',
            },
        },
        {
            element: '[data-tour="automacao-condicoes"]',
            panel: 'condicoes',
            popover: {
                title: 'Condições de acionamento',
                description: 'Aqui você define quando a automação deve ser acionada.',
                side: 'top',
                align: 'start',
            },
        },
        novaButtonStep(tourManager, {
            element: SELECTORS.condicaoNova,
            itemSelector: SELECTORS.condicaoItem,
            panel: 'condicoes',
            title: 'Nova condição',
            description: 'Clique em <strong>Nova condição</strong> para adicionar uma regra de acionamento para a automação a partir de um parâmetro do monitorado. As automações podem ter várias condições.',
        }),
        {
            element: '[data-tour="automacao-condicao-monitorado"]',
            panel: 'condicoes',
            ensureItem: (tm) => ensureExists(tm, SELECTORS.condicaoNova, SELECTORS.condicaoItem),
            popover: {
                title: 'Monitorado',
                description: 'Escolha o monitorado que será utilizado nesta condição.',
                side: 'bottom',
                align: 'start',
            },
        },
        {
            element: '[data-tour="automacao-condicao-campo"]',
            panel: 'condicoes',
            ensureItem: (tm) => ensureExists(tm, SELECTORS.condicaoNova, SELECTORS.condicaoItem),
            popover: {
                title: 'Campo',
                description: 'Selecione o parâmetro cadastrado no monitorado. Por exemplo, um status ou uma leitura do sensor.',
                side: 'bottom',
                align: 'start',
            },
        },
        {
            element: '[data-tour="automacao-condicao-operador"]',
            panel: 'condicoes',
            ensureItem: (tm) => ensureExists(tm, SELECTORS.condicaoNova, SELECTORS.condicaoItem),
            popover: {
                title: 'Operador',
                description: 'Informe como o valor será comparado: igual, diferente, maior, menor e assim por diante.',
                side: 'bottom',
                align: 'start',
            },
        },
        {
            element: '[data-tour="automacao-condicao-valor"]',
            panel: 'condicoes',
            ensureItem: (tm) => ensureExists(tm, SELECTORS.condicaoNova, SELECTORS.condicaoItem),
            popover: {
                title: 'Valor ou status',
                description: 'Digite o valor de comparação. Se o campo for de status, escolha <strong>Ativado</strong> ou <strong>Desativado</strong>.',
                side: 'bottom',
                align: 'start',
            },
        },
        {
            element: SELECTORS.condicaoOu,
            panel: 'condicoes',
            ensureItem: async (tm) => {
                await ensureExists(tm, SELECTORS.condicaoNova, SELECTORS.condicaoItem)
                try {
                    await tm.waitForElement(SELECTORS.condicaoOu, 2500)
                } catch (error) {}
            },
            popover: {
                title: 'Condição do tipo OU',
                description: 'Use <strong>Nova condição do tipo OU</strong>quando tiver mais de uma condição e quiser que a automação seja acionada quando qualquer uma delas for atendida.',
                side: 'top',
                align: 'end',
            },
        },
        {
            element: '[data-tour="automacao-condicoes-automacoes"]',
            panel: 'condicoes',
            popover: {
                title: 'Condições com outra automação',
                description: 'Use este bloco só quando a regra depender de outra automação.',
                side: 'top',
                align: 'start',
            },
        },
        novaButtonStep(tourManager, {
            element: SELECTORS.condicaoAutomacaoNova,
            itemSelector: SELECTORS.condicaoAutomacaoItem,
            panel: 'condicoes',
            title: 'Nova condição de automação',
            description: 'Em <strong>Nova condição</strong> adicionamos os campos desta regra.',
        }),
        {
            element: SELECTORS.condicaoAutomacaoItem,
            panel: 'condicoes',
            ensureItem: (tm) => ensureExists(tm, SELECTORS.condicaoAutomacaoNova, SELECTORS.condicaoAutomacaoItem),
            popover: {
                title: 'Campos da condição',
                description: 'Escolha o <strong>Monitorado</strong> e a <strong>Automação</strong>.<br>Em <strong>Tipo</strong>, use o status atual (Ativo ou Desativado) ou a quantidade de execuções no dia.',
                side: 'top',
                align: 'start',
            },
        },
        {
            element: '[data-tour="automacao-gatilhos"]',
            panel: 'condicoes',
            popover: {
                title: 'Gatilhos',
                description: 'Selecione os equipamentos que devem verificar as condições. Os equipamentos escolhidos enviam os dados usados para avaliar a automação.',
                side: 'top',
                align: 'start',
                onNextClick: () => moveToPanelAndNext(tourManager, 'acoes'),
                onPrevClick: () => tourManager.movePrevious(),
            },
        },
        {
            element: '[data-tour="automacao-acoes-positivas"]',
            panel: 'acoes',
            popover: {
                title: 'Ações positivas',
                description: 'Defina o que acontece quando as condições forem atendidas. No próximo passo, vamos adicionar uma ação.',
                side: 'top',
                align: 'start',
                onPrevClick: () => moveToPanelAndPrevious(tourManager, 'condicoes'),
            },
        },
        novaButtonStep(tourManager, {
            element: SELECTORS.acaoPositivaNova,
            itemSelector: SELECTORS.acaoPositivaItem,
            panel: 'acoes',
            title: 'Nova ação positiva',
            description: 'Clique em <strong>Nova ação</strong> para criar a ação e ver os campos.',
        }),
        {
            element: SELECTORS.acaoPositivaItem,
            panel: 'acoes',
            ensureItem: (tm) => ensureExists(tm, SELECTORS.acaoPositivaNova, SELECTORS.acaoPositivaItem),
            popover: {
                title: 'Campos da ação',
                description: 'Escolha o <strong>Tipo da Ação</strong>. Conforme a opção, os campos seguintes mudam:<br><br><strong>Enviar Notificação</strong>: selecione os contatos.<br><strong>Executar Comando</strong>: escolha o monitorado e o comando.<br><strong>Requisição HTTP</strong>: informe o método e o endereço.',
                side: 'top',
                align: 'start',
            },
        },
        {
            element: '[data-tour="automacao-acoes-negativas"]',
            panel: 'acoes',
            popover: {
                title: 'Ações negativas',
                description: 'Use este bloco para definir o que deve acontecer quando as condições não forem atendidas. Os tipos de ação são os mesmos do bloco anterior.',
                side: 'top',
                align: 'start',
                onNextClick: () => moveToPanelAndNext(tourManager, 'extras'),
            },
        },
        {
            element: '[data-tour="automacao-tolerancia"]',
            panel: 'extras',
            popover: {
                title: 'Tolerância',
                description: 'Informe quanto tempo a condição deve permanecer válida antes de acionar a automação. Use o formato de minutos e segundos.',
                side: 'bottom',
                align: 'start',
                onPrevClick: () => moveToPanelAndPrevious(tourManager, 'acoes'),
            },
        },
        {
            element: '[data-tour="automacao-dias"]',
            panel: 'extras',
            popover: {
                title: 'Dias permitidos',
                description: 'Escolha em quais dias a automação pode ser executada. Você pode usar uma opção pronta ou marcar os dias da semana manualmente.',
                side: 'top',
                align: 'start',
            },
        },
        {
            element: '[data-tour="automacao-intervalos"]',
            panel: 'extras',
            popover: {
                title: 'Intervalo de execução',
                description: 'Cadastre os horários em que a automação pode ser executada. No próximo passo, vamos adicionar um intervalo.',
                side: 'top',
                align: 'start',
                onNextClick: () => goNextCreating(tourManager, SELECTORS.intervaloNovo, SELECTORS.intervaloItem),
            },
        },
        {
            element: SELECTORS.intervaloItem,
            panel: 'extras',
            ensureItem: (tm) => ensureExists(tm, SELECTORS.intervaloNovo, SELECTORS.intervaloItem),
            popover: {
                title: 'Campos do intervalo',
                description: 'Informe a <strong>Hora Início</strong> e a <strong>Hora Fim</strong> em que a automação pode ser executada.',
                side: 'top',
                align: 'start',
            },
        },
        {
            element: SELECTORS.intervaloVolume,
            panel: 'extras',
            ensureItem: (tm) => ensureExists(tm, SELECTORS.intervaloNovo, SELECTORS.intervaloItem),
            popover: {
                title: 'Considerar volume do período',
                description: 'Marque esta opção quando a condição deve levar em conta o volume acumulado neste horário, e não só o valor de um instante. Assim a plataforma interpreta o total do período, por exemplo o consumo entre o início e o fim.',
                side: 'left',
                align: 'start',
                onNextClick: () => moveToPanelAndNext(tourManager, 'simulacao'),
            },
        },
        {
            element: '[data-tour="automacao-simulacao"]',
            panel: 'simulacao',
            popover: {
                title: 'Simulação',
                description: 'Use esta tela para revisar as condições, as ações, os dias de funcionamento e os intervalos de execução.',
                side: 'top',
                align: 'start',
                onPrevClick: () => moveToPanelAndPrevious(tourManager, 'extras'),
            },
        },
        {
            element: '[data-tour="automacao-simulacao-teste"], .automacao-simulacao.row',
            panel: 'simulacao',
            popover: {
                title: 'Teste de lógica',
                description: 'Esta área permite testar como a plataforma interpreta as condições da automação, <strong>sem executar a ação configurada</strong>.<br><br>Informe valores de exemplo ou utilize a última leitura disponível para verificar se as condições seriam atendidas. Assim, você pode revisar a lógica da automação antes de salvá-la.',
                side: 'left',
                align: 'start',
            },
        },
        {
            element: '[data-tour="automacao-salvar"]',
            panel: 'simulacao',
            popover: {
                title: 'Salvar automação',
                description: 'Depois de preencher e revisar os campos, clique em <strong>Confirmar e Salvar</strong>. Se preferir apenas concluir o guia, use o botão <strong>Concluir</strong>.',
                side: 'top',
                align: 'end',
                onPrevClick: () => tourManager.movePrevious(),
            },
        },
    ]

    return formSteps.map((step) => ({
        ...step,
        ensureReady: async (tm) => {
            await activatePanel(step.panel)
            if (typeof step.ensureItem === 'function') {
                await step.ensureItem(tm)
            }
        },
    }))
}

export const buildNovoAutomacaoFormSteps = (tourManager, options) => buildFormSteps(tourManager, false, options)

export const buildEditarAutomacaoFormSteps = (tourManager) => buildFormSteps(tourManager, true)
